import React, { useState, useEffect, useContext } from 'react';
import { Card, Row, Col, Button, ListGroup, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaUserClock, FaUsers, FaBuilding, FaProjectDiagram, FaPlus } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    customers: [],
    totalCustomers: 0,
    totalUsers: 0,
    totalProjects: 0,
    pendingSignups: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  
  const { token } = useContext(AuthContext);
  
  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Get customers
        const customersResponse = await fetch('/api/admin/customers', {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!customersResponse.ok) {
          throw new Error('Failed to fetch customers');
        }
        
        const customersData = await customersResponse.json();
        
        // Get pending signups count
        let pendingCount = 0;
        try {
          const pendingResponse = await fetch('/api/onboarding/pending', {
            headers: { 'x-auth-token': token }
          });
          if (pendingResponse.ok) {
            const pendingData = await pendingResponse.json();
            pendingCount = pendingData.length;
          }
        } catch (pendingErr) {
          console.log('Could not fetch pending signups:', pendingErr);
        }
        
        // Calculate total users and projects
        let totalUsers = 0;
        let totalProjects = 0;
        
        // Only process up to 5 customers for the dashboard to avoid too many requests
        const processLimit = Math.min(customersData.length, 5);
        const processedCustomers = [];
        
        for (let i = 0; i < processLimit; i++) {
          const customer = customersData[i];
          
          try {
            // Get users for this customer
            const usersResponse = await fetch(`/api/admin/${customer.CompanyName}/users`, {
              headers: {
                'x-auth-token': token
              }
            });
            
            if (usersResponse.ok) {
              const usersData = await usersResponse.json();
              totalUsers += usersData.length;
              customer.userCount = usersData.length;
            } else {
              customer.userCount = 0;
            }
            
            // Get projects for this customer
            const projectsResponse = await fetch(`/api/admin/${customer.CompanyName}/projects`, {
              headers: {
                'x-auth-token': token
              }
            });
            
            if (projectsResponse.ok) {
              const projectsData = await projectsResponse.json();
              totalProjects += projectsData.length;
              customer.projectCount = projectsData.length;
            } else {
              customer.projectCount = 0;
            }
            
            processedCustomers.push(customer);
          } catch (err) {
            console.error(`Error processing customer ${customer.CompanyName}:`, err);
            // Still add the customer but with zero counts
            customer.userCount = 0;
            customer.projectCount = 0;
            processedCustomers.push(customer);
          }
        }
        
        setStats({
          customers: processedCustomers,
          totalCustomers: customersData.length,
          totalUsers,
          totalProjects,
          pendingSignups: pendingCount
        });
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        toast.error('Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchStats();
  }, [token]);
  
  if (isLoading) {
    return <div className="text-center mt-5">Loading dashboard...</div>;
  }
  
  return (
    <div>
      <h2 className="mb-4">Admin Dashboard</h2>
      
      {/* Pending Signups Alert - Show prominently if there are pending signups */}
      {stats.pendingSignups > 0 && (
        <Card className="mb-4 border-warning" style={{ borderWidth: '2px' }}>
          <Card.Body className="bg-warning bg-opacity-10">
            <Row className="align-items-center">
              <Col>
                <div className="d-flex align-items-center">
                  <FaUserClock size={40} className="text-warning me-3" />
                  <div>
                    <h4 className="mb-1">
                      {stats.pendingSignups} Pending Signup{stats.pendingSignups !== 1 ? 's' : ''}
                    </h4>
                    <p className="mb-0 text-muted">New customer{stats.pendingSignups !== 1 ? 's are' : ' is'} waiting for approval</p>
                  </div>
                </div>
              </Col>
              <Col xs="auto">
                <Button as={Link} to="/admin/pending-signups" variant="warning" size="lg">
                  Review & Approve
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
      
      <Row className="mb-4">
        {/* Pending Signups Card */}
        <Col md={3}>
          <Card className={`h-100 ${stats.pendingSignups > 0 ? 'border-warning' : ''}`}>
            <Card.Body className="text-center">
              <FaUserClock size={30} className={stats.pendingSignups > 0 ? 'text-warning mb-2' : 'text-secondary mb-2'} />
              <Card.Title>Pending Signups</Card.Title>
              <Card.Text className="display-4">
                {stats.pendingSignups > 0 ? (
                  <Badge bg="warning" className="fs-1">{stats.pendingSignups}</Badge>
                ) : (
                  <span className="text-muted">0</span>
                )}
              </Card.Text>
              <Button 
                as={Link} 
                to="/admin/pending-signups" 
                variant={stats.pendingSignups > 0 ? 'warning' : 'outline-secondary'}
                size="sm"
              >
                {stats.pendingSignups > 0 ? 'Review Now' : 'View Pending'}
              </Button>
            </Card.Body>
          </Card>
        </Col>
        
        {/* Customers Card */}
        <Col md={3}>
          <Card className="h-100">
            <Card.Body className="text-center">
              <FaBuilding size={30} className="text-primary mb-2" />
              <Card.Title>Customers</Card.Title>
              <Card.Text className="display-4">{stats.totalCustomers}</Card.Text>
              <Button as={Link} to="/admin/customers" variant="outline-primary" size="sm">
                View All
              </Button>
            </Card.Body>
          </Card>
        </Col>
        
        {/* Users Card */}
        <Col md={3}>
          <Card className="h-100">
            <Card.Body className="text-center">
              <FaUsers size={30} className="text-success mb-2" />
              <Card.Title>Total Users</Card.Title>
              <Card.Text className="display-4">{stats.totalUsers}</Card.Text>
              <Button as={Link} to="/admin/users" variant="outline-success" size="sm">
                Manage Users
              </Button>
            </Card.Body>
          </Card>
        </Col>
        
        {/* Projects Card */}
        <Col md={3}>
          <Card className="h-100">
            <Card.Body className="text-center">
              <FaProjectDiagram size={30} className="text-info mb-2" />
              <Card.Title>Total Projects</Card.Title>
              <Card.Text className="display-4">{stats.totalProjects}</Card.Text>
              <Button as={Link} to="/admin/customers" variant="outline-info" size="sm">
                View Projects
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Quick Actions */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Quick Actions</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={3} className="mb-2">
                  <Button as={Link} to="/admin/customers/create" variant="primary" className="w-100">
                    <FaPlus className="me-2" /> Add Customer
                  </Button>
                </Col>
                <Col md={3} className="mb-2">
                  <Button as={Link} to="/admin/pending-signups" variant="outline-warning" className="w-100">
                    <FaUserClock className="me-2" /> Pending Signups
                    {stats.pendingSignups > 0 && (
                      <Badge bg="warning" text="dark" className="ms-2">{stats.pendingSignups}</Badge>
                    )}
                  </Button>
                </Col>
                <Col md={3} className="mb-2">
                  <Button as={Link} to="/admin/users" variant="outline-success" className="w-100">
                    <FaUsers className="me-2" /> Manage Users
                  </Button>
                </Col>
                <Col md={3} className="mb-2">
                  <Button as={Link} to="/admin/customers" variant="outline-primary" className="w-100">
                    <FaBuilding className="me-2" /> All Customers
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Customer Overview */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Recent Customers</h5>
          <Button as={Link} to="/admin/customers/create" variant="primary" size="sm">
            <FaPlus className="me-1" /> Add Customer
          </Button>
        </Card.Header>
        <Card.Body>
          {stats.customers.length > 0 ? (
            <ListGroup>
              {stats.customers.map(customer => (
                <ListGroup.Item key={customer.CustomerId} className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-0">{customer.CompanyName}</h6>
                    <small className="text-muted">
                      {customer.Email} | {customer.Phone || 'No phone'}
                    </small>
                  </div>
                  <div className="text-end">
                    <Badge bg="primary" className="me-2">
                      {customer.userCount} Users
                    </Badge>
                    <Badge bg="success">
                      {customer.projectCount} Projects
                    </Badge>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <p className="text-center text-muted mb-0">No customers found. Add your first customer.</p>
          )}
          
          {stats.totalCustomers > 5 && (
            <div className="text-center mt-3">
              <Button as={Link} to="/admin/customers" variant="outline-primary">
                View All {stats.totalCustomers} Customers
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default AdminDashboard;
